import React, { useState, useEffect, useMemo } from "react";
import { Alert, Modal, Button, Form, Space, Result, Select } from "antd";
import { useStepsForm } from "sunflower-antd";

import { SelectProps } from "antd/lib/select";

import { getMemberApi } from "../../../services/Member/MemberApis";
import {
  getGeneratedDocumentsMissingLink,
  getSendableMemberDocuments,
  validateSelectedDocumentsForSend,
} from "../../../utils/documentCompliance";

interface IModalProps {
  show: boolean;
  showModal: Function;
  handleSend?: Function | undefined;
  memberId: any;
}

type MemberDocuments = {
  id?: string;
  Name?: string;
  Link?: string;
  DocumentSent?: boolean | null;
  DocumentGenerated?: boolean;
};

const messageType: SelectProps["options"] = [
  {
    name: "SMS",
    label: "SMS",
    value: "SMS",
  },
];

const SendDocumentModal: React.FC<IModalProps> = ({
  show,
  showModal,
  handleSend,
  memberId,
}) => {
  const [memberDocuments, setMemberDocuments] = useState<MemberDocuments[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const sendableDocuments = useMemo(
    () => getSendableMemberDocuments(memberDocuments),
    [memberDocuments]
  );

  const documentsMissingLink = useMemo(
    () => getGeneratedDocumentsMissingLink(memberDocuments),
    [memberDocuments]
  );

  const documentOptions: SelectProps["options"] = sendableDocuments.map(
    (doc) => ({
      value: doc.id,
      label: doc.Name,
    })
  );

  const handleModal = () => {
    showModal();
    gotoStep(0);
    form.resetFields();
    setErrorMsg("");
  };

  const {
    form,
    current,
    gotoStep,
    formProps,
    submit,
  } = useStepsForm({
    isBackValidate: false,
    async submit(values: any) {
      const validation = validateSelectedDocumentsForSend(
        values.Documents,
        memberDocuments
      );

      if (!validation.isValid) {
        setErrorMsg(validation.message ?? "Selected documents cannot be sent.");
        setLoading(false);
        return false;
      }

      setLoading(true);
      if (handleSend != undefined) {
        const response = await handleSend(memberId, values);
        if (response?.success) {
          setLoading(false);
          return true;
        }
        setErrorMsg(response?.message ?? "Failed to send documents.");
        setLoading(false);
        return false;
      }
      setLoading(false);
      return false;
    },
    total: 3,
  });

  const layout = {
    labelCol: { span: 8 },
    wrapperCol: { span: 8 },
  };

  const tailLayout = {
    labelCol: { span: 8 },
    wrapperCol: { offset: 8, span: 8 },
  };

  const display: any = {
    0: (
      <>
        {documentsMissingLink.length > 0 && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="Documents missing download links"
            description={`Some generated documents have no download link. Regenerate them before sending: ${documentsMissingLink
              .map((doc) => doc?.Name ?? "Unnamed document")
              .join(", ")}.`}
          />
        )}
        {sendableDocuments.length === 0 && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="No documents ready to send"
            description="Generate a document with a valid link first, then return here to send it."
          />
        )}
        <Form.Item
          name="Documents"
          label="Documents"
          rules={[{ required: true, message: "Please select at least one document" }]}
        >
          <Select
            mode="multiple"
            id="Documents"
            options={documentOptions}
            size="large"
            style={{ width: "100%" }}
            placeholder="Documents"
            disabled={sendableDocuments.length === 0}
          />
        </Form.Item>

        <Form.Item
          name="MessageType"
          label="Message Type"
          rules={[{ required: true, message: "Please select a message type" }]}
        >
          <Select
            mode="multiple"
            options={messageType}
            size="large"
            style={{ width: "100%" }}
            placeholder="Message Type"
            disabled={sendableDocuments.length === 0}
          />
        </Form.Item>

        <Form.Item {...tailLayout}>
          <Space direction="vertical" style={{ width: "100%" }}>
            <Button
              loading={loading}
              disabled={sendableDocuments.length === 0}
              style={{ marginBottom: 10 }}
              block
              size="large"
              onClick={() => {
                submit().then((result: any) => {
                  if (result) {
                    gotoStep(current + 1);
                  } else if (result === false) {
                    gotoStep(current + 2);
                  }
                });
              }}
            >
              Send Document
            </Button>
          </Space>
        </Form.Item>
      </>
    ),
  };

  useEffect(() => {
    if (!show || !memberId) return;

    getMemberApi(memberId)
      .then((res) => {
        setMemberDocuments(res.data.PersonalDocument ?? []);
      })
      .catch((err) => {
        console.log(err);
        setMemberDocuments([]);
      });
  }, [show, memberId]);

  return (
    <Modal
      title="Send Documents"
      open={show}
      onOk={handleModal}
      onCancel={handleModal}
      width={900}
      footer={null}
    >
      <div>
        {current === 1 && <></>}
        <div style={{ marginTop: 60 }}>
          <Form {...layout} {...formProps} style={{ maxWidth: 900 }}>
            {display[current]}
          </Form>

          {current === 1 && (
            <Result status="success" title="Successfully Sent Documents.">
              <Space direction="vertical" style={{ width: "100%" }}>
                <Button block size="large" onClick={handleModal}>
                  Okay
                </Button>
              </Space>
            </Result>
          )}

          {current === 2 && (
            <Result status="error" title={errorMsg}>
              <Space direction="vertical" style={{ width: "100%" }}>
                <Button block size="large" onClick={handleModal}>
                  Okay
                </Button>
              </Space>
            </Result>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default SendDocumentModal;
